<template>
    <!-- Water detail modal: the figures behind a section's Water Index. A headline card carries the
         index value and which day it was actually computed from, a secondary card carries the raw
         actual-vs-norm calculation inputs for that same day, and a chart shows the day-by-day actual
         vs norm water consumption from Production Group start through today. Shell (teleport/overlay/
         header/prev-next/Escape+Arrow keys) lives in DetailModalShell, shared with
         WeightDetailModal/MortalityDetailModal; the pig-tally chart below is PigTallyChart, shared
         with MortalityDetailModal. -->
    <DetailModalShell :section="section" :sections="sections" @close="emit('close')" @navigate="onNavigate" @handle-alarm="onHandleAlarm">
        <template #default="{ section: activeSection }">
            <!-- Headline card: the Water Index value shown in the grid, labelled with which
                 day it was actually computed from (server-computed, never recomputed here).
                 Coloured via the same indexColourClass banding as the Growth Index headline
                 card (WeightDetailModal) - Water Index shares that unified index scheme. -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                    {{ $t('views.monitoring.activeGroups.modals.waterDetail.title') }}
                </h3>
                <p
                    class="mt-2 text-[28px] font-extrabold leading-tight tracking-tight tabular-nums"
                    :class="activeSection.waterIndex !== null ? indexColourClass(activeSection.waterIndex) : 'text-gray-300'"
                >
                    {{ activeSection.waterIndex !== null ? activeSection.waterIndex : placeholder }}
                </p>
                <p v-if="dayLabel" class="text-[11px] text-gray-400">{{ dayLabel }}</p>
            </div>

            <!-- Calculation inputs behind the index: the raw actual/norm figures for the same
                 day, composing into the same value shown above (delivered values reused, never
                 recomputed client-side). -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                    {{ $t('views.monitoring.activeGroups.modals.waterDetail.figuresTitle') }}
                </h3>
                <div
                    v-if="activeSection.waterActualWater !== null && activeSection.waterNormWater !== null"
                    class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-gray-100 px-3 py-2 tabular-nums"
                >
                    <div class="text-center">
                        <p class="text-sm font-bold leading-tight text-gray-800">{{ $n(activeSection.waterActualWater, 'decimal') }}</p>
                        <p class="text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.waterDetail.actualWater') }}
                        </p>
                    </div>
                    <span class="text-sm font-semibold text-gray-300">÷</span>
                    <div class="text-center">
                        <p class="text-sm font-bold leading-tight text-gray-800">{{ $n(activeSection.waterNormWater, 'decimal') }}</p>
                        <p class="text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400">
                            {{ $t('views.monitoring.activeGroups.modals.waterDetail.normWater') }}
                        </p>
                    </div>
                    <span class="text-sm font-semibold text-gray-300">× 100 =</span>
                    <p class="text-base font-extrabold leading-tight text-gray-900">
                        {{ activeSection.waterIndex !== null ? activeSection.waterIndex : placeholder }}
                    </p>
                </div>
                <p v-else class="mt-2 text-[11.5px] text-gray-400">{{ placeholder }}</p>
            </div>

            <!-- Water consumption: actual vs norm from Production Group start through today. Unlike
                 the mortality modal's 12-months pig-tally series, the backend never returns
                 history beyond the current Production Group, so there is no navigator/range picker
                 here — the chart always shows the full series. -->
            <div class="rounded-lg bg-white p-4 shadow-sm">
                <div class="mb-2 flex items-center gap-2">
                    <h3 class="flex-1 truncate text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                        {{ $t('views.monitoring.activeGroups.modals.waterDetail.chartTitle') }}
                    </h3>
                    <!-- Same amber-band signal as the mortality modal's negative-tally warning,
                         ported to count_unstable days instead of a negative pig balance. -->
                    <span
                        v-if="hasUnstableDays"
                        class="cursor-help"
                        :title="$t('views.monitoring.activeGroups.modals.waterDetail.chartUnstableWarning')"
                    >
                        <AlertTriangle class="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                    </span>
                </div>
                <div v-if="isSeriesLoading" class="flex items-center justify-center py-16">
                    <Spinner class="h-6 w-6" />
                </div>
                <GenericChart v-else-if="waterSeries.length" :options="(chartOptions as any)" :expandable="false" />
                <p v-else class="py-6 text-center text-xs italic text-gray-400">
                    {{ $t('views.monitoring.activeGroups.modals.waterDetail.chartEmpty') }}
                </p>
            </div>

            <!-- Pig tally: same chart as the mortality modal (the current Production Group by default, 12
                 months of history through the navigator, day/week granularity, reset), reusing
                 its usePigTallySeries data — the water index moves with the herd, so the tally
                 behind it belongs alongside the norm chart above. -->
            <PigTallyChart :section-id="activeSection.id" :day="activeSection.day" :directory-local-now="activeSection.directoryLocalNow" />
        </template>
    </DetailModalShell>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { AlertTriangle } from 'lucide-vue-next';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { useWaterNormSeries } from '@/plugins/store/actions/queries/active-groups';
import { directoryLocalDayStart } from './directoryLocalNow';
import type { SectionRow } from './sectionRow';
import { indexColourClass } from './indexColour';
import { computeFlagBands } from './flagBands';
import { formatActiveGroupsChartTooltipHeader, activeGroupsWeekAxisLabels } from './activeGroupsChartFormatting';
import DetailModalShell from './DetailModalShell.vue';
import PigTallyChart from './PigTallyChart.vue';

const placeholder = '—';

const props = defineProps<{
    // The row whose Water Index cell was clicked (null = closed).
    section: SectionRow | null;
    // All rows whose Water Index cell can open this modal, in the page's display order — drives
    // prev/next navigation. The parent stays in control: navigation only emits the target row.
    sections: SectionRow[];
}>();

const emit = defineEmits<{
    close: [];
    navigate: [section: SectionRow];
    handleAlarm: [section: SectionRow];
}>();

function onNavigate(target: SectionRow): void {
    emit('navigate', target);
}

function onHandleAlarm(target: SectionRow): void {
    emit('handleAlarm', target);
}

// On-demand water-norm fetch: enabled only while the modal is open (section != null).
const sectionId = computed(() => props.section?.id ?? null);
const { data: waterData, isLoading: isSeriesLoading } = useWaterNormSeries(sectionId);
const waterSeries = computed(() => waterData.value ?? []);

// The date the currently-displayed index was actually computed from (yesterday, or two days ago
// when yesterday's reading was unstable/missing — the backend's existing fallback). Empty when
// waterAsOfDate is unset (no usable day found, mirrors when the index itself is null).
const dayLabel = computed(() => {
    if (!props.section?.waterAsOfDate) return '';
    return i18n.global.d(parseISO(props.section.waterAsOfDate), 'middle') as string;
});

const hasUnstableDays = computed(() => waterSeries.value.some((point) => point.count_unstable));

// The unstable-day stretches as faint amber bands across the chart (same signal as the warning
// glyph next to the title).
const unstableBands = computed(() => computeFlagBands(waterSeries.value, (point) => Date.parse(point.date), (point) => point.count_unstable));

// App-wide chart conventions (localized axis, fonts, series palette) come from the shared wrapper;
// the custom-tooltip flag skips its per-point tooltip so the shared one below can show both series'
// values in a single box. No `type: ChartType.Stock` here — unlike the mortality modal's 12-months
// pig-tally series, the water-norm series never extends beyond the current Production Group (the
// backend always returns exactly Production Group-start → today), so there's nothing to navigate to.
const chartDefaults = useDefaultChartOptions({}, {}, true);

const chartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, chartDefaults.value);

    return merge(base, {
        chart: {
            height: 260,
            spacingLeft: 0,
            spacingRight: 0,
            backgroundColor: 'transparent',
            marginRight: 8,
        },
        exporting: { enabled: false },
        legend: { enabled: true, itemStyle: { color: '#6B7280', fontSize: '11px', fontWeight: '500' } },
        xAxis: {
            // Overrides the shared wrapper's global period-picker range: the series is already
            // scoped to the section's own Production Group, so it should always render in full.
            min: null,
            // The present moment, not the last datapoint - a Sensor that stopped reporting
            // days ago must render as empty space up to the right edge, not as a shortened axis.
            max: directoryLocalDayStart(props.section?.directoryLocalNow ?? null),
            labels: activeGroupsWeekAxisLabels,
            plotBands: unstableBands.value,
        },
        yAxis: {
            softMin: 0,
        },
        tooltip: {
            shared: true,
            split: false,
            formatter(this: any): string {
                const header = `<span style="font-size: 10px">${formatActiveGroupsChartTooltipHeader(this.x, this.points?.[0]?.series?.chart?.time)}</span>`;
                return (this.points ?? []).reduce(
                    (acc: string, point: any) =>
                        `${acc}<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n(point.y, 'decimal')}</b>`,
                    header
                );
            },
        },
        series: [
            {
                type: 'column',
                name: i18n.global.t('views.monitoring.activeGroups.modals.waterDetail.chartActualWater') as string,
                data: waterSeries.value.map((point) => [Date.parse(point.date), point.actual_water]),
                borderRadius: 1,
            },
            {
                type: 'line',
                name: i18n.global.t('views.monitoring.activeGroups.modals.waterDetail.chartNormWater') as string,
                data: waterSeries.value.map((point) => [Date.parse(point.date), point.norm_water]),
                marker: { enabled: false },
            },
        ],
    });
});
</script>
